<template>
  <div id="chat_browser" class="flex flex-col h-full">
    <div
      id="chat_browser_header"
      class="flex w-full items-center justify-between p-3 border_bottom"
    >
      <div class="flex flex-col gap-3 w-full">
        <div class="relative">
          <CustomInput
            ref="searchInputRef"
            v-model="searchQuery"
            placeholder="Search chats"
            type="primary"
            :icon="imageNames.SEARCH_VARIANT1"
            size="size-4"
            @input="debouncedSearch"
            @clear="handleSearchClear"
            @focusin="handleSearchFocus"
          />

          <div
            v-if="showSearchDropdown"
            ref="searchDropdownRef"
            class="absolute top-full left-0 right-0 mt-1 max-h-[320px] overflow-y-auto z-50 rounded-lg border border-[var(--white-opacity-12)] bg-[var(--background-color)] shadow-lg"
            @scroll="handleSearchDropdownScroll"
          >
            <div v-if="searchLoading && searchResults.length === 0" class="flex items-center justify-center p-3">
              <span class="text-[13px] secondary_color">Searching...</span>
            </div>
            <div v-else-if="searchResults.length === 0 && searchQuery" class="flex items-center justify-center p-3">
              <span class="text-[13px] secondary_color">No chats found</span>
            </div>
            <template v-else>
              <div
                v-for="(chat, index) in searchResults"
                :key="`search_${chat?.id}_${index}`"
                class="flex flex-row gap-2 items-center py-2 px-3 cursor-pointer transition-colors duration-150 hover:bg-[var(--white-opacity-8)]"
                @click="handleSearchSelect(chat)"
              >
                <div
                  v-if="isGroupDM(chat)"
                  class="relative w-[26px] h-[26px] shrink-0"
                >
                  <ProfileIcon
                    :size="22"
                    :font="8"
                    :data="getGroupDMMembers(chat)[0]"
                    class="absolute -top-1 -left-1 z-[2]"
                  />
                  <ProfileIcon
                    :size="23"
                    :font="8"
                    :data="getGroupDMMembers(chat)[1]"
                    class="absolute bottom-0 right-0 z-[3] border-2 border-[var(--background-color)]"
                  />
                </div>
                <div
                  v-else-if="isChannelGroup(chat) && chat.profile_pic"
                  class="shrink-0 w-6 h-6 rounded-full overflow-hidden"
                >
                  <img :src="chat.profile_pic" alt="group avatar" class="w-full h-full object-cover" />
                </div>
                <div
                  v-else-if="isChannelGroup(chat)"
                  class="flex items-center justify-center rounded-full shrink-0 w-6 h-6"
                  :class="getAuthUserProfileColor({ name: chat.name }).class"
                  :style="getAuthUserProfileColor({ name: chat.name }).style || {}"
                >
                  <ThemeImage
                    :icon="imageNames.GROUP"
                    alt="group_icon"
                    class="size-4"
                  />
                </div>
                <ProfileIcon v-else :size="26" :font="10" :data="getChatProfileData(chat)" />

                <span class="text-[13px] truncate primary_color">{{ chat.name || "Unknown" }}</span>
              </div>
              <div v-if="searchLoadingMore" class="flex items-center justify-center p-3">
                <span class="text-[13px] secondary_color">Loading more...</span>
              </div>
            </template>
          </div>
        </div>

        <div class="flex flex-row justify-between items-center gap-2">
          <div class="flex-1 min-w-0 overflow-x-auto hide-scrollbar">
            <CustomTabs
              v-model="activeKey"
              :tabs="chatTabs"
              content-padding="p-0"
              tabHeaderClass="max-w-[150px]"
              extraHeaderStyles="overflow-x-visible"
              @update:modelValue="(event) => handleTabUpdate(event)"
            />
          </div>

          <a-tooltip position="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">Create new</span>
            </template>

            <ThemeImage
                class="size-5 cursor-pointer shrink-0"
                :icon="imageNames.NEW_CHAT"
                alt="create_new_icon"
                @click="handleCreateNew"
            />
          </a-tooltip>
        </div>
      </div>
    </div>

    <div
      id="chat_browser_body"
      ref="scrollContainer"
      :style="isEmbeddedMode ? {} : { height: 'calc(100vh - (150px + var(--banner-height)))' }"
      :class="[isEmbeddedMode ? 'flex-1 min-h-0' : '', 'overflow-y-scroll']"
    >
      <SkeletonLoader
        v-if="fetchingChats"
        :page="loaderPages.CHAT_BROWSER_PANEL"
      />

      <ChatList v-else-if="chats?.length > 0" :chats="chats" />

      <EmptyScreen v-else source="chat_browser" text="No chats found!" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, inject, nextTick } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  chatTabs as staticChatTabs,
  chatViews,
  chatTabOptions,
  chatTypes,
  chatRecipientTypes,
} from "@/src/constants/chatConstants.js";
import { renderEntity, getAuthUserProfileColor } from "@/src/utils/utils.js";
import debounce from "lodash/debounce.js";
import eventBus from "@/src/utils/eventBus.js";
import ChatService from "@/src/api/ChatService.js";
import CustomTabs from "@/src/components/CustomTabs.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ActionWrapper from "@/src/components/custom_components/ActionWrapper.vue";
import ChatList from "@/src/views/projects/chats/side_panel/browser/ChatList.vue";
import DashboardService from "@/src/api/DashboardService.js";

const emit = defineEmits(["toggle-view"]);
const store = useStore();
const isEmbeddedMode = inject("embeddedChatMode", false);
const openEmbeddedChat = inject("openEmbeddedChat", null);
const fetchingChats = ref(false);
const loadingMore = ref(false);
const searchQuery = ref("");
const searchInputRef = ref(null);
const activeKey = ref(chatTabOptions.ALL);
const scrollContainer = ref(null);
const draftsVersion = ref(0);

const totalUnreadCount = computed(() => store.getters["chats/totalUnreadCount"] || 0);

const chatTabs = computed(() => {
  return staticChatTabs.map((tab) =>
    tab.id === chatTabOptions.UNREAD && totalUnreadCount.value > 0
      ? { ...tab, count: totalUnreadCount.value }
      : tab,
  );
});

const pagination = ref({
  limit: 500,
  offset: 0,
  hasMore: true,
  total: 0,
});

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

// Search dropdown state
const showSearchDropdown = ref(false);
const searchDropdownRef = ref(null);
const searchResults = ref([]);
const searchLoading = ref(false);
const searchLoadingMore = ref(false);
const searchPagination = ref({
  limit: 100,
  offset: 0,
  hasMore: true,
});

// Version counter to discard stale search responses
let searchRequestVersion = 0;

// Cache for initial (no-query) dropdown results — 1 hour TTL
const SEARCH_CACHE_TTL = 60 * 60 * 1000;
const initialSearchCache = ref({ data: null, timestamp: 0, hasMore: true });

const getDraftChatIds = () => {
  const draftIds = new Set();
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('chat_draft_') && !key.includes('thread_')) {
      const chatId = key.replace('chat_draft_', '');
      try {
        const draftData = JSON.parse(localStorage.getItem(key) || '{}');
        if (draftData.text?.trim()) {
          draftIds.add(chatId);
        }
      } catch (e) {
      }
    }
  }
  
  return draftIds;
};

const pinnedGroupIds = computed(() => store.getters["chats/pinnedGroupIds"]);

const sortWithPinnedFirst = (list) => {
  const pinned = [];
  const unpinned = [];
  for (const group of list) {
    if (pinnedGroupIds.value.has(String(group.id))) {
      pinned.push(group);
    } else {
      unpinned.push(group);
    }
  }
  return [...pinned, ...unpinned];
};

// Filter out the digital twin's DM from the chat list
const twinDmGroupId = ref(null);
const isTwinDmGroup = (group) => {
  if (twinDmGroupId.value && String(group.id) === String(twinDmGroupId.value)) return true;
  return false;
};

const chats = computed(() => {
  let groups = (store.getters["chats/groups"] || []).filter(g => !isTwinDmGroup(g));

  if (activeKey.value === chatTabOptions.DRAFTS) {
    draftsVersion.value;
    const draftIds = getDraftChatIds();
    return sortWithPinnedFirst(groups.filter(group => draftIds.has(String(group.id))));
  }

  if (activeKey.value === chatTabOptions.DMS) {
    return sortWithPinnedFirst(groups.filter(group =>
      group.is_dm === true ||
      group.type === chatTypes.DM ||
      group.type === chatTypes.SELF_DM ||
      group.type === chatTypes.GROUP_DM
    ));
  }

  if (activeKey.value === chatTabOptions.GROUPS) {
    return sortWithPinnedFirst(groups.filter(group =>
      group.type === chatTypes.PUBLIC ||
      group.type === chatTypes.PRIVATE
    ));
  }

  if (activeKey.value === chatTabOptions.EXTERNAL) {
    return sortWithPinnedFirst(groups.filter(group =>
      group.type === chatTypes.EXTERNAL
    ));
  }

  if (activeKey.value === chatTabOptions.ASSOCIATED) {
    return sortWithPinnedFirst(groups.filter(group =>
      !!group.entity_type && !!group.entity_id
    ));
  }

  if (activeKey.value === chatTabOptions.UNREAD) {
    return sortWithPinnedFirst(groups.filter(group =>
      (group.unread_count || 0) > 0
    ));
  }

  // "All" tab: show everything including external groups
  return sortWithPinnedFirst(groups);
});

const handleTabUpdate = (data) => {
  try {
    activeKey.value = data;
    store.dispatch("chats/setActiveChatTab", data);

    // Clear search state on tab switch
    debouncedSearch.cancel();
    showSearchDropdown.value = false;
    searchQuery.value = "";
    searchResults.value = [];

    pagination.value = {
      limit: 500,
      offset: 0,
      hasMore: true,
      total: 0,
    };

    toFetchChats();
  } catch (error) {
    console.error("An error occurred while handling tab update: ", error);
  }
};

const handleSwitchTab = async (tab) => {
  if (activeKey.value !== tab) {
    activeKey.value = tab;
    store.dispatch("chats/setActiveChatTab", tab);

    // Clear search state on tab switch
    debouncedSearch.cancel();
    showSearchDropdown.value = false;
    searchQuery.value = "";
    searchResults.value = [];

    pagination.value = {
      limit: 500,
      offset: 0,
      hasMore: true,
      total: 0,
    };

    await toFetchChats();
  }
  eventBus.emit("switch-chat-tab-done");
};

// Maps tab keys to the API type parameter (client-only tabs have no API type)
const getSearchTypeParam = () => {
  const tab = activeKey.value;
  if (!tab || tab === chatTabOptions.ALL || tab === chatTabOptions.DRAFTS || tab === chatTabOptions.UNREAD) {
    return null;
  }
  // associated, dms, groups, external are valid API type values
  return tab;
};

const fetchSearchResults = async (loadMore = false) => {
  const isSearchQuery = !!searchQuery.value;

  // For no-query initial load, serve from cache if fresh
  if (!isSearchQuery && !loadMore) {
    const cache = initialSearchCache.value;
    if (cache.data && cache.tab === activeKey.value && (Date.now() - cache.timestamp) < SEARCH_CACHE_TTL) {
      searchResults.value = cache.data;
      searchPagination.value.offset = cache.data.length;
      searchPagination.value.hasMore = cache.hasMore;
      return;
    }
  }

  // Increment version for non-loadMore requests to detect stale responses
  const thisVersion = loadMore ? searchRequestVersion : ++searchRequestVersion;

  try {
    if (loadMore) {
      searchLoadingMore.value = true;
    } else {
      searchLoading.value = true;
      searchPagination.value.offset = 0;
    }

    const queryParams = {
      limit: isSearchQuery ? 500 : searchPagination.value.limit,
      offset: loadMore ? searchPagination.value.offset : 0,
    };

    if (isSearchQuery) {
      queryParams.search = searchQuery.value;
    }

    // Pass the active tab's type so search respects the current filter
    const typeParam = getSearchTypeParam();
    if (typeParam) {
      queryParams.type = typeParam;
    }

    const response = await ChatService.fetchGroups(queryParams);

    // Discard stale responses — a newer request has been fired
    if (thisVersion !== searchRequestVersion) {
      return;
    }

    if (response?.success) {
      // Filter out I-Assistant twin groups — only accessible via the I-Assistant tab
      const newResults = (response?.conversations || response?.groups || [])
        .filter(g => !g.name?.startsWith("I-Assistant"));

      if (loadMore) {
        searchResults.value = [...searchResults.value, ...newResults];
      } else {
        searchResults.value = newResults;
      }
      searchPagination.value.offset = loadMore
        ? searchPagination.value.offset + newResults.length
        : newResults.length;
      searchPagination.value.hasMore = response?.pagination?.has_more ?? false;

      // Cache initial (no-query) results per tab
      if (!isSearchQuery && !loadMore) {
        initialSearchCache.value = {
          data: newResults,
          tab: activeKey.value,
          timestamp: Date.now(),
          hasMore: searchPagination.value.hasMore,
        };
      }
    }
  } catch (error) {
    console.error("An error occurred while fetching search results: ", error);
  } finally {
    // Only clear loading state if this is still the latest request
    if (thisVersion === searchRequestVersion) {
      searchLoading.value = false;
      searchLoadingMore.value = false;
    }
  }
};

const debouncedSearch = debounce(() => {
  showSearchDropdown.value = true;
  fetchSearchResults();
}, 300);

const handleSearchFocus = () => {
  showSearchDropdown.value = true;
  fetchSearchResults();
};

const handleSearchClear = () => {
  debouncedSearch.cancel();
  showSearchDropdown.value = false;
  searchResults.value = [];
};

const handleSearchDropdownScroll = (event) => {
  const container = event.target;
  const { scrollTop, scrollHeight, clientHeight } = container;
  const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

  if (isNearBottom && !searchLoadingMore.value && !searchLoading.value && searchPagination.value.hasMore) {
    fetchSearchResults(true);
  }
};

const getTabForChat = (chat) => {
  if (chat.type === chatTypes.EXTERNAL) return chatTabOptions.EXTERNAL;
  if (chat.entity_type && chat.entity_id) return chatTabOptions.ASSOCIATED;
  if (chat.is_dm || chat.type === chatTypes.DM || chat.type === chatTypes.SELF_DM || chat.type === chatTypes.GROUP_DM) return chatTabOptions.DMS;
  if (chat.type === chatTypes.PUBLIC || chat.type === chatTypes.PRIVATE) return chatTabOptions.GROUPS;
  return chatTabOptions.ALL;
};

const handleSearchSelect = async (chat) => {
  debouncedSearch.cancel();
  showSearchDropdown.value = false;
  searchQuery.value = "";
  searchResults.value = [];

  // Switch to the correct tab for this chat type if not on All tab
  const correctTab = getTabForChat(chat);
  if (activeKey.value !== chatTabOptions.ALL && activeKey.value !== correctTab) {
    activeKey.value = chatTabOptions.ALL;
    store.dispatch("chats/setActiveChatTab", chatTabOptions.ALL);
    pagination.value = { limit: 500, offset: 0, hasMore: true, total: 0 };
    await toFetchChats();
  }

  // Check if the group is already in the chat list
  const groups = store.getters["chats/groups"] || [];
  let existingGroup = groups.find(g =>
    String(g.id).replace(/^group_/, '') === String(chat.id).replace(/^group_/, '')
  );

  // For placeholder chats, also check if a real DM already exists for this user
  if (!existingGroup && chat.is_placeholder) {
    const otherUser = chat.other_user || chat.user_data;
    const otherUserId = otherUser?.id;
    const otherUserName = otherUser ? renderEntity(otherUser).toLowerCase().trim() : null;

    existingGroup = groups.find(g => {
      if (g.is_placeholder || !g.is_dm || g.recipient_type !== 1) return false;
      if (g.type !== "dm" && g.type !== "self_dm") return false;
      // Match by member user ID if available
      if (otherUserId && g.members?.length > 0) {
        return g.members.some(m => Number(m.user_id || m.user?.id) === Number(otherUserId));
      }
      // Match by chat name
      if (otherUserName && g.name) {
        return g.name.toLowerCase().trim() === otherUserName;
      }
      return false;
    });
  }

  const selectedChat = existingGroup || chat;
  if (isEmbeddedMode && openEmbeddedChat) {
    openEmbeddedChat(selectedChat);
  } else {
    store.dispatch("chats/setActiveChat", selectedChat);
  }

  // Scroll to the chat in the list
  nextTick(() => {
    const chatListEl = document.getElementById("chat_list");
    if (!chatListEl) return;
    const chatId = chat.id;
    const activeEl = chatListEl.querySelector(`[data-chat-id="${chatId}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  });
};

const handleClickOutsideDropdown = (event) => {
  if (
    showSearchDropdown.value &&
    searchDropdownRef.value &&
    !searchDropdownRef.value.contains(event.target) &&
    searchInputRef.value?.$el &&
    !searchInputRef.value.$el.contains(event.target)
  ) {
    debouncedSearch.cancel();
    showSearchDropdown.value = false;
  }
};

const toFetchChats = async (params = {}) => {
  try {
    const isLoadingMore = params.loadMore;

    if (isLoadingMore) {
      loadingMore.value = true;
    } else {
      fetchingChats.value = true;
      pagination.value.offset = 0;
    }

    const queryParams = {
      limit: pagination.value.limit,
      offset: isLoadingMore ? pagination.value.offset : 0,
    };

    if (activeKey.value && activeKey.value !== "all" && activeKey.value !== chatTabOptions.DRAFTS) {
      queryParams.type = activeKey.value;
    }

    const response = await ChatService.fetchGroups(queryParams);

    if (response?.success) {
      const newChats = response?.conversations || response?.groups || [];
      const currentChats = store.getters["chats/groups"] || [];

      const isAllTab = !activeKey.value || activeKey.value === "all";

      if (isLoadingMore) {
        store.dispatch("chats/setGroups", {
          groups: [...currentChats, ...newChats],
          skipUnreadRecalc: !isAllTab,
        });
      } else {
        const currentActiveChat = store.getters["chats/activeChat"];
        const currentActiveChatId = currentActiveChat
          ? String(currentActiveChat.id).replace(/^group_/, '')
          : null;

        store.dispatch("chats/setGroups", {
          groups: newChats,
          skipUnreadRecalc: !isAllTab,
        });

        if (!isEmbeddedMode) {
          if (!currentActiveChatId) {
            if (newChats.length > 0) {
              store.dispatch("chats/setActiveChat", newChats[0]);
            }
          } else {
            const activeStillExists = newChats.some(chat =>
              String(chat.id).replace(/^group_/, '') === currentActiveChatId
            );

            if (!activeStillExists && newChats.length > 0) {
              store.dispatch("chats/setActiveChat", newChats[0]);
            }
          }
        }
      }

      pagination.value.offset = isLoadingMore
        ? pagination.value.offset + pagination.value.limit
        : pagination.value.limit;
      pagination.value.hasMore = response?.pagination?.has_more ?? false;
      pagination.value.total = response?.pagination?.total_count ?? 0;
    }
  } catch (error) {
    console.error("An error occurred while fetching chats: ", error);
  } finally {
    fetchingChats.value = false;
    loadingMore.value = false;
  }
};

const handleScroll = (event) => {
  try {
    const container = event.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (
      isNearBottom &&
      !loadingMore.value &&
      !fetchingChats.value &&
      pagination.value.hasMore
    ) {
      toFetchChats({ loadMore: true });
    }
  } catch (error) {
    console.error("An error occurred while handling scroll: ", error);
  }
};

const handleGroupRefresh = async (newGroup = null) => {
  try {
    // Switch to External tab if an external group was created
    if (newGroup?.type === chatTypes.EXTERNAL && activeKey.value !== chatTabOptions.EXTERNAL) {
      activeKey.value = chatTabOptions.EXTERNAL;
      store.dispatch("chats/setActiveChatTab", chatTabOptions.EXTERNAL);
    }

    pagination.value = {
      limit: 500,
      offset: 0,
      hasMore: true,
      total: 0,
    };

    await toFetchChats();

    if (newGroup) {
      const groups = store.getters["chats/groups"] || [];
      let groupInList = groups.find(g =>
        String(g.id).replace(/^group_/, '') === String(newGroup.id).replace(/^group_/, '')
      );

      // If the new group isn't in the fetched list, add it directly
      if (!groupInList) {
        await store.dispatch("chats/addGroupToList", newGroup);
        groupInList = newGroup;
      }

      if (isEmbeddedMode && openEmbeddedChat) {
        openEmbeddedChat(groupInList);
      } else {
        store.dispatch("chats/setActiveChat", groupInList);
      }
    }
  } catch (error) {
    console.error("An error occurred while refreshing groups: ", error);
  }
};

const handleCreateNew = () => {
  try {
    emit("toggle-view", chatViews.CREATE_NEW);
  } catch (error) {
    console.error("An error occurred while handling create new: ", error);
  }
};

const handleDraftsUpdate = () => {
  draftsVersion.value++;
};

const isGroupDM = (chat) => {
  return chat?.type === chatTypes.GROUP_DM;
};

const isChannelGroup = (chat) => {
  return !chat?.is_dm && (chat?.type === "public" || chat?.type === "private" || chat?.type === "external");
};

const resolveUserByName = (name) => {
  if (!name) return null;
  const normalizedName = name.toLowerCase().trim();
  return allUsers.value.find(u => renderEntity(u).toLowerCase().trim() === normalizedName) || null;
};

const getGroupDMMembers = (chat) => {
  if (chat?.members?.length > 0) {
    return chat.members
      .map(m => {
        const storeUser = allUsers.value.find(u => Number(u.id) === Number(m.user_id));
        return storeUser || { id: m.user_id, name: m.user_name, avatar_url: m.profile_pic };
      })
      .slice(0, 2);
  }
  if (!chat?.name) return [];
  const nameSegments = chat.name.split(",").map(s => s.trim()).filter(Boolean);
  const resolved = [];
  for (const segment of nameSegments) {
    if (resolved.length >= 2) break;
    const user = resolveUserByName(segment);
    if (user && Number(user.id) !== Number(currentUserId.value)) {
      resolved.push(user);
    }
  }
  return resolved;
};

const getChatProfileData = (chat) => {
  if (!chat) return chat;
  const isOneOnOneDM = chat?.is_dm === true &&
    (chat?.type === chatTypes.DM || chat?.type === chatTypes.SELF_DM) &&
    chat?.recipient_type === chatRecipientTypes.ONE_ON_ONE;
  if (isOneOnOneDM && chat.name) {
    if (chat.type === chatTypes.SELF_DM) {
      const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
      if (currentUser) return currentUser;
    }
    const storeUser = resolveUserByName(chat.name);
    if (storeUser) return storeUser;
  }
  return chat;
};

onMounted(async () => {
  // Hide I-Assistant group from chat list
  const storedTwinDmId = sessionStorage.getItem("twin_dm_group_id");
  if (storedTwinDmId) {
    twinDmGroupId.value = storedTwinDmId;
  } else {
    try {
      const twinStatus = await DashboardService.getTwinStatus();
      if (twinStatus.has_digital_twin && twinStatus.assistant_group_id) {
        twinDmGroupId.value = String(twinStatus.assistant_group_id);
      }
    } catch (e) { /* ignore */ }
  }

  const allUsers = store.getters["users/users"] || [];
  if (allUsers.length === 0) {
    store.dispatch("users/fetchUsers");
  }

  toFetchChats();
  store.dispatch("chats/fetchPinnedGroups");

  if (scrollContainer.value) {
    scrollContainer.value.addEventListener("scroll", handleScroll);
  }

  document.addEventListener("mousedown", handleClickOutsideDropdown);

  eventBus.on("group-created", handleGroupRefresh);
  eventBus.on("group-updated", handleGroupRefresh);
  eventBus.on("group-deleted", handleGroupRefresh);
  eventBus.on("message-sent", handleDraftsUpdate);
  eventBus.on("draft-updated", handleDraftsUpdate);
  eventBus.on("switch-chat-tab", handleSwitchTab);
});

onUnmounted(() => {
  debouncedSearch.cancel();

  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener("scroll", handleScroll);
  }

  document.removeEventListener("mousedown", handleClickOutsideDropdown);

  eventBus.off("group-created", handleGroupRefresh);
  eventBus.off("group-updated", handleGroupRefresh);
  eventBus.off("group-deleted", handleGroupRefresh);
  eventBus.off("message-sent", handleDraftsUpdate);
  eventBus.off("draft-updated", handleDraftsUpdate);
  eventBus.off("switch-chat-tab", handleSwitchTab);
});
</script>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
